import React, { useState, useMemo } from 'react';
import { X, ChevronRight, ChevronLeft, CreditCard, Users, Utensils, Sparkles, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPOSIT = 500;
const PRICE_1 = 1800;
const PRICE_2 = 1800 + 1620; // 10% off second guest
const ENERGY_ADDON = 3000;
const NARRATIVE_ADDON_LABEL = "Inquire";
const CATERED_PRICE = 50;

const CareBnBBookingForm = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    // Step 1: Guests
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    whatsapp: '',
    age: '',
    isMidrashaStudent: '',
    midrashaName: '',
    numGuests: '1',
    partnerFirstName: '',
    partnerLastName: '',
    partnerEmail: '',

    // Step 2: Dates + Dietary + Add-ons
    preferredDate: '',
    alternateDate: '',
    dietaryRestrictions: '',
    allergies: '',
    goalsExpectations: '',
    addonEnergy: false,
    addonNarrative: false,
    addonCateredMeals: 0,

    // Step 3: Confirmation
    depositConfirmed: false,
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const totals = useMemo(() => {
    const guests = formData.numGuests === '2' ? 2 : 1;
    const retreatBase = guests === 2 ? PRICE_2 : PRICE_1;
    const energy = formData.addonEnergy ? ENERGY_ADDON : 0;
    const catered = (Number(formData.addonCateredMeals) || 0) * CATERED_PRICE;
    const total = retreatBase + energy + catered;
    const balance = Math.max(total - DEPOSIT, 0);
    return { guests, retreatBase, energy, catered, total, balance };
  }, [formData.numGuests, formData.addonEnergy, formData.addonCateredMeals]);

  const validate = () => {
    if (currentStep === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        toast.error('Please fill in your name and email');
        return false;
      }
      if (!formData.phone && !formData.whatsapp) {
        toast.error('Please provide a phone or WhatsApp number');
        return false;
      }
      if (formData.numGuests === '2' && (!formData.partnerFirstName || !formData.partnerLastName)) {
        toast.error("Please share your friend's / partner's name");
        return false;
      }
      return true;
    }
    if (currentStep === 2) {
      if (!formData.preferredDate) {
        toast.error('Please choose a preferred Thursday date');
        return false;
      }
      if (!formData.dietaryRestrictions && !formData.allergies) {
        toast.error('Please tell us about dietary restrictions or allergies (write "None" if none)');
        return false;
      }
      return true;
    }
    if (currentStep === 3) {
      if (!formData.depositConfirmed) {
        toast.error('Please confirm you understand the deposit');
        return false;
      }
      return true;
    }
    return true;
  };

  const next = () => {
    if (validate() && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const prev = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  const submit = async () => {
    if (!validate()) return;
    try {
      setSubmitting(true);
      const payload = { ...formData, totals, program: 'shabbat_chavaya' };
      const { data: booking } = await axios.post(`${API}/carebnb/bookings`, payload);

      toast.success('Booking saved! Redirecting to secure deposit checkout...');

      const { data: checkout } = await axios.post(`${API}/checkout/session`, {
        submission_id: booking.submission_id,
        package_id: 'carebnb_deposit',
        origin_url: window.location.origin,
      });

      if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    if (currentStep === 1) {
      return (
        <div className="form-step">
          <h2 className="heading-2">Who's coming?</h2>
          <p className="body-small text-muted">Book for yourself or bring a friend/partner (max 2 guests, no kids).</p>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input type="text" className="form-input" value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} data-testid="cb-first-name" />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input type="text" className="form-input" value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} data-testid="cb-last-name" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input type="email" className="form-input" value={formData.email} onChange={(e) => updateField('email', e.target.value)} data-testid="cb-email" />
            </div>
            <div className="form-group">
              <label className="form-label">Age</label>
              <input type="number" className="form-input" value={formData.age} onChange={(e) => updateField('age', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input type="tel" className="form-input" value={formData.phone} onChange={(e) => updateField('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">WhatsApp</label>
              <input type="tel" className="form-input" value={formData.whatsapp} onChange={(e) => updateField('whatsapp', e.target.value)} data-testid="cb-whatsapp" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Are you a midrasha student?</label>
            <div className="checkbox-group">
              {['Yes', 'No'].map((opt) => (
                <label key={opt} className="checkbox-label">
                  <input type="radio" name="midrasha" checked={formData.isMidrashaStudent === opt} onChange={() => updateField('isMidrashaStudent', opt)} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {formData.isMidrashaStudent === 'Yes' && (
            <div className="form-group">
              <label className="form-label">Which midrasha?</label>
              <input type="text" className="form-input" value={formData.midrashaName} onChange={(e) => updateField('midrashaName', e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Number of guests *</label>
            <div className="guest-toggle">
              {['1', '2'].map((n) => (
                <button
                  type="button"
                  key={n}
                  className={`guest-toggle-btn ${formData.numGuests === n ? 'active' : ''}`}
                  onClick={() => updateField('numGuests', n)}
                  data-testid={`cb-guests-${n}`}
                >
                  <Users size={16} />
                  <span>{n === '1' ? '1 guest · Solo' : '2 guests · Friends/Couple'}</span>
                  <strong>{n === '1' ? `${PRICE_1.toLocaleString()} NIS` : `${PRICE_2.toLocaleString()} NIS`}</strong>
                </button>
              ))}
            </div>
          </div>

          {formData.numGuests === '2' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Second guest first name *</label>
                <input type="text" className="form-input" value={formData.partnerFirstName} onChange={(e) => updateField('partnerFirstName', e.target.value)} data-testid="cb-partner-first" />
              </div>
              <div className="form-group">
                <label className="form-label">Second guest last name *</label>
                <input type="text" className="form-input" value={formData.partnerLastName} onChange={(e) => updateField('partnerLastName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Second guest email</label>
                <input type="email" className="form-input" value={formData.partnerEmail} onChange={(e) => updateField('partnerEmail', e.target.value)} />
              </div>
            </div>
          )}
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="form-step">
          <h2 className="heading-2">Dates, food & add-ons</h2>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Preferred Thursday arrival date *</label>
              <input type="date" className="form-input" value={formData.preferredDate} onChange={(e) => updateField('preferredDate', e.target.value)} data-testid="cb-preferred-date" />
            </div>
            <div className="form-group">
              <label className="form-label">Alternate Thursday</label>
              <input type="date" className="form-input" value={formData.alternateDate} onChange={(e) => updateField('alternateDate', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Dietary restrictions *</label>
            <p className="caption">Vegan, kosher-only, gluten-free, low-fodmap, etc. (Write "None" if none.)</p>
            <textarea className="form-textarea" rows="3" value={formData.dietaryRestrictions} onChange={(e) => updateField('dietaryRestrictions', e.target.value)} data-testid="cb-dietary" />
          </div>

          <div className="form-group">
            <label className="form-label">Allergies *</label>
            <p className="caption">Nuts, sesame, dairy, gluten, sulfites, etc. Please be thorough.</p>
            <textarea className="form-textarea" rows="3" value={formData.allergies} onChange={(e) => updateField('allergies', e.target.value)} data-testid="cb-allergies" />
          </div>

          <div className="form-group">
            <label className="form-label">What are you hoping to gain from this getaway?</label>
            <textarea className="form-textarea" rows="3" value={formData.goalsExpectations} onChange={(e) => updateField('goalsExpectations', e.target.value)} />
          </div>

          {/* Add-ons */}
          <div className="addons-selection">
            <h3 className="heading-3">Optional add-ons</h3>

            <label className={`addon-toggle ${formData.addonEnergy ? 'active' : ''}`} data-testid="cb-addon-energy">
              <input type="checkbox" checked={formData.addonEnergy} onChange={(e) => updateField('addonEnergy', e.target.checked)} />
              <Sparkles size={20} color="var(--brand-lilac-deep)" />
              <div>
                <strong>Energy Therapy · 6 Session Package</strong>
                <p className="caption">3,000 NIS (was 3,570)</p>
              </div>
            </label>

            <label className={`addon-toggle ${formData.addonNarrative ? 'active' : ''}`} data-testid="cb-addon-narrative">
              <input type="checkbox" checked={formData.addonNarrative} onChange={(e) => updateField('addonNarrative', e.target.checked)} />
              <BookOpen size={20} color="var(--brand-teal)" />
              <div>
                <strong>Narrative Therapy Session</strong>
                <p className="caption">Chanah will follow up with pricing after booking</p>
              </div>
            </label>

            <div className="addon-toggle" style={{ cursor: 'default' }}>
              <Utensils size={20} color="var(--brand-gold)" />
              <div style={{ flex: 1 }}>
                <strong>Catered Meals</strong>
                <p className="caption">50 NIS per meal</p>
                <input
                  type="number"
                  min="0"
                  max="12"
                  className="form-input"
                  style={{ marginTop: 8, maxWidth: 120 }}
                  value={formData.addonCateredMeals}
                  onChange={(e) => updateField('addonCateredMeals', Math.max(0, Number(e.target.value) || 0))}
                  data-testid="cb-addon-catered-qty"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Step 3: Review + Deposit
    return (
      <div className="form-step">
        <h2 className="heading-2">Review & Reserve</h2>

        <div className="cb-review-card">
          <h3 className="heading-3">{formData.firstName} {formData.lastName}
            {formData.numGuests === '2' && <> + {formData.partnerFirstName} {formData.partnerLastName}</>}
          </h3>
          <p className="body-small">{formData.email} · {formData.phone || formData.whatsapp}</p>
          <p className="body-small">Preferred: <strong>{formData.preferredDate}</strong>{formData.alternateDate && <> · alt {formData.alternateDate}</>}</p>
        </div>

        <div className="cb-cost-breakdown" data-testid="cb-cost-breakdown">
          <div className="cost-row">
            <span>Retreat ({totals.guests} guest{totals.guests > 1 ? 's' : ''})</span>
            <strong>{totals.retreatBase.toLocaleString()} NIS</strong>
          </div>
          {formData.addonEnergy && (
            <div className="cost-row">
              <span>Energy Therapy add-on</span>
              <strong>{ENERGY_ADDON.toLocaleString()} NIS</strong>
            </div>
          )}
          {totals.catered > 0 && (
            <div className="cost-row">
              <span>{formData.addonCateredMeals} catered meal{formData.addonCateredMeals > 1 ? 's' : ''}</span>
              <strong>{totals.catered.toLocaleString()} NIS</strong>
            </div>
          )}
          {formData.addonNarrative && (
            <div className="cost-row">
              <span>Narrative therapy</span>
              <strong style={{ color: 'var(--brand-teal)' }}>Chanah will follow up</strong>
            </div>
          )}
          <div className="cost-row total">
            <span>Total</span>
            <strong>{totals.total.toLocaleString()} NIS</strong>
          </div>
          <div className="cost-row deposit-highlight">
            <span>Deposit today (secure your spot)</span>
            <strong>{DEPOSIT.toLocaleString()} NIS</strong>
          </div>
          <div className="cost-row">
            <span className="body-small">Balance due upon arrival</span>
            <strong className="body-small">{totals.balance.toLocaleString()} NIS</strong>
          </div>
        </div>

        <label className="checkbox-label-block">
          <input
            type="checkbox"
            checked={formData.depositConfirmed}
            onChange={(e) => updateField('depositConfirmed', e.target.checked)}
            data-testid="cb-deposit-confirm"
          />
          <span>I understand that I'm paying a <strong>{DEPOSIT} NIS deposit</strong> now to secure my booking, and the balance is due upon arrival. Cancellation policy applies as per the intake consent.</span>
        </label>
      </div>
    );
  };

  return (
    <div className="form-overlay" data-testid="carebnb-booking-modal">
      <div className="form-container">
        <div className="form-header">
          <div>
            <h1 className="heading-2">Book Your CareBnB Getaway</h1>
            <p className="body-small text-muted">Step {currentStep} of {totalSteps}</p>
          </div>
          <button onClick={onClose} className="btn-close" data-testid="cb-form-close">
            <X size={24} />
          </button>
        </div>

        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }} />
        </div>

        <div className="form-content">
          {renderStep()}
        </div>

        <div className="form-footer">
          {currentStep > 1 && (
            <button onClick={prev} className="btn-secondary" data-testid="cb-prev" disabled={submitting}>
              <ChevronLeft size={20} />
              <span>Previous</span>
            </button>
          )}
          <div className="flex-spacer" />
          {currentStep < totalSteps ? (
            <button onClick={next} className="btn-primary" data-testid="cb-next">
              <span>Next</span>
              <ChevronRight size={20} />
            </button>
          ) : (
            <button
              onClick={submit}
              className="btn-primary"
              data-testid="cb-submit-pay"
              disabled={submitting || !formData.depositConfirmed}
            >
              <CreditCard size={20} />
              <span>{submitting ? 'Processing…' : `Pay ${DEPOSIT} NIS Deposit`}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CareBnBBookingForm;
